import React, { FC, memo } from "react";
import { useTranslation } from "react-i18next";

import { Header } from "@/components/Header";
import { SubSection, TSubSectionRole } from "@/components/Section";
import { Text } from "@/components/Text";
import { IConsultantProfile, IProfileCompleteness, TCompletenessItemKey } from "@/data/consultant-profiles";
import { Flex, Img } from "@/eworkitComponents";

import congratulation from "../../assets/congratulation.png";
import { getProfileDataKeys, isCompletenessItemCompleted } from "../../service";
import { ProgressItem } from "./components/ProgressItem";

import "./_progress-widget.scss";

interface IProgressWidgetProps extends Pick<IProfileCompleteness, "completed"> {
  consultantProfileId: IConsultantProfile["id"];
  role?: TSubSectionRole;
  onUpdate: (profile: IConsultantProfile) => void;
  isPrivateProfile: boolean;
}

const BASE_CLASS = "completeness-progress-widget";

export const ProgressWidget: FC<IProgressWidgetProps> = memo(({ completed, role, isPrivateProfile }) => {
  const { t } = useTranslation("session");

  const isCompleted = (item: TCompletenessItemKey) => isCompletenessItemCompleted(item, completed);

  const isProfileSectionCompleted = !getProfileDataKeys(isPrivateProfile).some(k => !isCompleted(k));

  const renderProfileSection = () => {
    if (isProfileSectionCompleted) {
      return (
        <Flex direction="column" justify="center" padding={["lg"]} innerMargin="sm">
          <Flex margin={[false, false, "md", false]} justify="center">
            <Img src={congratulation} className={`${BASE_CLASS}__img`} />
          </Flex>
          <Text align="center" size="xl">
            {t("CONSULTANT_PROFILES.COMPLETENESS.CONTENT.PROFILE.COMPLETED_TITLE")}
          </Text>
          <Text align="center" color="grey3">
            {t("CONSULTANT_PROFILES.COMPLETENESS.CONTENT.PROFILE.COMPLETED_TEXT")}
          </Text>
        </Flex>
      );
    }

    return getProfileDataKeys(isPrivateProfile).map(key => {
      const completedLvl = isCompleted(key);

      const text = completedLvl
        ? t("CONSULTANT_PROFILES.COMPLETENESS.DONE")
        : t(`CONSULTANT_PROFILES.COMPLETENESS.CONTENT.PROFILE.${key}.POINTS`);

      return (
        <ProgressItem
          key={key}
          title={t(`CONSULTANT_PROFILES.COMPLETENESS.CONTENT.PROFILE.${key}.TITLE`)}
          text={text}
          completed={completedLvl}
        />
      );
    });
  };

  return (
    <SubSection
      role={role}
      background="background-card"
      padding={[false, "lg", "lg", "lg"]}
      shadow="none"
      className={BASE_CLASS}>
      <Header size="sm" weight="bold">
        {t("CONSULTANT_PROFILES.COMPLETENESS.WIDGET.HEADER")}
      </Header>
      <Flex direction="column" padding={["md", false, false, false]}>
        {renderProfileSection()}
      </Flex>
    </SubSection>
  );
});
